import type { CoreApi } from '#shared/core'
import type { RedirectToQuery } from '#shared/utils/auth'
import type { FetchOptions } from 'ofetch'
import { array, nullish, object, parse, string } from 'valibot'

// Plain function signature (no NitroFetchRequest generic) so calls never trigger Nuxt's
// typed-route matching — these paths proxy an external Core API and never match this app's own routes.
export type CoreFetch = (request: string, options?: FetchOptions) => Promise<unknown>

export interface CoreApiFetchers {
  ecommerce: CoreFetch
  external: CoreFetch
}

const contextSchema = nullish(
  object({
    updatedCookies: nullish(
      array(string()),
    ),
  }),
)

const AUTH_PATH_PREFIXES = ['/logg-inn', '/callback'] as const

export function buildLoginNavigation(fullPath: string): { name: 'login', query: RedirectToQuery } {
  const isAuthPage = AUTH_PATH_PREFIXES.some(path => fullPath.startsWith(path))
  const query: RedirectToQuery = isAuthPage ? {} : { redirectTo: fullPath }
  return { name: 'login', query }
}

/**
 * Provides fetchers for Core APIs.
 */
export default defineNuxtPlugin((app) => {
  const headers = useRequestHeaders(['cookie'])

  const event = useRequestEvent()

  // Uses Nuxt's real `$fetch` at runtime (needed for correct relative-URL resolution during SSR).
  // Cast (via `unknown`) to a plain function type to avoid the typed-route matching recursion.
  const createFetcher = (api: CoreApi) => $fetch.create({
    baseURL: `/api/core/${api}`,

    // Ensure cookies are passed to the server route during SSR.
    headers,

    async onRequest({ options }) {
      // Check if updated cookies has been provided through the event context, and if so, update the headers before sending the request.
      const context = parse(contextSchema, event?.context)
      if (context?.updatedCookies?.length) {
        options.headers.set('cookie', context.updatedCookies.join('; '))
      }
    },

    async onResponseError({ response }) {
      // If we receive a 401 response, which should only happen if the refresh token has expired, redirect to the login page.
      if (response.status === 401) {
        await app.runWithContext(() => {
          const route = useRoute()
          const { name, query } = buildLoginNavigation(route.fullPath)
          return navigateTo({ name, query })
        })
      }
    },
  })

  return {
    provide: {
      coreApi: {
        ecommerce: createFetcher('ecommerce') as unknown as CoreFetch,
        external: createFetcher('external') as unknown as CoreFetch,
      } satisfies CoreApiFetchers,
    },
  }
})
